import React, { useState, useMemo } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import {
  ThumbsUp,
  Target,
  AlertTriangle,
  ThumbsDown,
  MessageSquare,
  Lightbulb,
  Zap,
  FileText,
  Image as ImageIcon,
  BarChart3,
  Rocket,
  Calendar,
  Bell,
  Trophy,
  Bot,
  CheckCircle2,
  XCircle,
  UserPlus,
  DollarSign,
  FolderGit2,
  Building2,
  Send,
  Hash,
  Pin,
  Share2,
  Download,
  Filter,
  Search,
  ChevronDown,
  Sparkles,
  ExternalLink,
  Plus,
  ShieldCheck,
  Cpu,
  Layers
  ,X
} from 'lucide-react';
import {
  PulseEventItem,
  PulseEventType,
  PulseReaction,
  PulseReply,
} from '../../data/pulseData';
import { useUI } from '../../context/UIContext';
import { useNavigation } from '../../context/NavigationContext';
import { useAuth } from '../../context/AuthContext';
import UserProfileModal, { UserProfileData } from './UserProfileModal';
import { PulseComment } from '../../services/pulse/pulse.types';

export interface ActivityFeedProps {
  filterModule?: string;
  contextEntityId?: string | number;
  showHeaderSearch?: boolean;
  compactMode?: boolean;
  onPostCreated?: (event: any) => void;
  events?: any[];
  isLoading?: boolean;
  feedError?: Error | null;
  onRetryFeed?: () => void;
  onPublishPost?: (content: string, type: PulseEventType, hashtags?: string[]) => Promise<void>;
  onAddReaction?: (eventId: string, reactionKey: string) => Promise<void>;
  onRemoveReaction?: (eventId: string, reactionKey: string) => Promise<void>;
  onAddComment?: (eventId: string, text: string, parentId?: string) => Promise<void>;
  onEditPost?: (eventId: string, content: string) => Promise<void>;
  onDeletePost?: (eventId: string) => Promise<void>;
  onTogglePin?: (eventId: string, pinned: boolean) => Promise<void>;
  comments?: Record<string, PulseComment[]>;
  isLoadingComments?: boolean;
}

const EVENT_TYPE_CONFIG: Record<
  PulseEventType,
  {
    label: string;
    icon: React.ElementType;
    badgeBg: string;
    badgeText: string;
    badgeBorder: string;
    emoji: string;
  }
> = {
  comment: {
    label: 'Comentário',
    icon: MessageSquare,
    badgeBg: 'bg-slate-100/80',
    badgeText: 'text-slate-700',
    badgeBorder: 'border-slate-200/80',
    emoji: '💬'
  },
  idea: {
    label: 'Ideia',
    icon: Lightbulb,
    badgeBg: 'bg-amber-50',
    badgeText: 'text-amber-800',
    badgeBorder: 'border-amber-200/60',
    emoji: '💡'
  },
  decision: {
    label: 'Decisão',
    icon: ShieldCheck,
    badgeBg: 'bg-emerald-50',
    badgeText: 'text-emerald-800',
    badgeBorder: 'border-emerald-200/60',
    emoji: '✅'
  },
  document: {
    label: 'Documento',
    icon: FileText,
    badgeBg: 'bg-sky-50',
    badgeText: 'text-sky-800',
    badgeBorder: 'border-sky-200/60',
    emoji: '📄'
  },
  image: {
    label: 'Imagem',
    icon: ImageIcon,
    badgeBg: 'bg-indigo-50',
    badgeText: 'text-indigo-800',
    badgeBorder: 'border-indigo-200/60',
    emoji: '🖼'
  },
  indicator: {
    label: 'Indicador',
    icon: BarChart3,
    badgeBg: 'bg-emerald-50',
    badgeText: 'text-emerald-800',
    badgeBorder: 'border-emerald-200/60',
    emoji: '📊'
  },
  deploy: {
    label: 'Deploy',
    icon: Rocket,
    badgeBg: 'bg-blue-50',
    badgeText: 'text-blue-800',
    badgeBorder: 'border-blue-200/60',
    emoji: '🚀'
  },
  event: {
    label: 'Evento',
    icon: Calendar,
    badgeBg: 'bg-violet-50',
    badgeText: 'text-violet-800',
    badgeBorder: 'border-violet-200/60',
    emoji: '📅'
  },
  warning: {
    label: 'Aviso',
    icon: Bell,
    badgeBg: 'bg-rose-50',
    badgeText: 'text-rose-800',
    badgeBorder: 'border-rose-200/60',
    emoji: '🔔'
  },
  achievement: {
    label: 'Conquista',
    icon: Trophy,
    badgeBg: 'bg-amber-100/60',
    badgeText: 'text-amber-900',
    badgeBorder: 'border-amber-300/80',
    emoji: '🏆'
  },
  ai: {
    label: 'IA Intel',
    icon: Bot,
    badgeBg: 'bg-teal-50',
    badgeText: 'text-teal-800',
    badgeBorder: 'border-teal-200/60',
    emoji: '🤖'
  },
  approval: {
    label: 'Aprovação',
    icon: CheckCircle2,
    badgeBg: 'bg-emerald-50',
    badgeText: 'text-emerald-800',
    badgeBorder: 'border-emerald-200/60',
    emoji: '✅'
  },
  rejection: {
    label: 'Rejeição',
    icon: XCircle,
    badgeBg: 'bg-rose-50',
    badgeText: 'text-rose-800',
    badgeBorder: 'border-rose-200/60',
    emoji: '❌'
  },
  new_member: {
    label: 'Novo Colaborador',
    icon: UserPlus,
    badgeBg: 'bg-indigo-50',
    badgeText: 'text-indigo-800',
    badgeBorder: 'border-indigo-200/60',
    emoji: '👥'
  },
  finance: {
    label: 'Financeiro',
    icon: DollarSign,
    badgeBg: 'bg-emerald-50',
    badgeText: 'text-emerald-800',
    badgeBorder: 'border-emerald-200/60',
    emoji: '💰'
  },
  project: {
    label: 'Projeto',
    icon: FolderGit2,
    badgeBg: 'bg-blue-50',
    badgeText: 'text-blue-800',
    badgeBorder: 'border-blue-200/60',
    emoji: '📋'
  },
  client: {
    label: 'Cliente',
    icon: Building2,
    badgeBg: 'bg-purple-50',
    badgeText: 'text-purple-800',
    badgeBorder: 'border-purple-200/60',
    emoji: '🏢'
  },
  suggestion: {
    label: 'Sugestão',
    icon: Lightbulb,
    badgeBg: 'bg-amber-50',
    badgeText: 'text-amber-800',
    badgeBorder: 'border-amber-200/60',
    emoji: '💡'
  }
};

const CORPORATE_REACTIONS = [
  { key: 'agree', icon: ThumbsUp, label: 'Concordo', color: 'text-emerald-600', bg: 'bg-emerald-50', borderColor: 'border-emerald-200' },
  { key: 'excellent', icon: Rocket, label: 'Excelente', color: 'text-indigo-600', bg: 'bg-indigo-50', borderColor: 'border-indigo-200' },
  { key: 'goodIdea', icon: Lightbulb, label: 'Boa Ideia', color: 'text-amber-600', bg: 'bg-amber-50', borderColor: 'border-amber-200' },
  { key: 'attention', icon: AlertTriangle, label: 'Atenção', color: 'text-orange-600', bg: 'bg-orange-50', borderColor: 'border-orange-200' },
  { key: 'approved', icon: CheckCircle2, label: 'Aprovado', color: 'text-teal-600', bg: 'bg-teal-50', borderColor: 'border-teal-200' },
  { key: 'priority', icon: Target, label: 'Prioritário', color: 'text-rose-600', bg: 'bg-rose-50', borderColor: 'border-rose-200' }
] as const;

export default function ActivityFeed({
  filterModule,
  events: externalEvents,
  isLoading = false,
  feedError = null,
  onRetryFeed,
  onPublishPost,
  onAddReaction,
  onRemoveReaction,
  onAddComment,
  onEditPost,
  onDeletePost,
  onTogglePin,
  onPostCreated,
  comments = {},
  isLoadingComments = false,
}: ActivityFeedProps) {
  const { setCurrentView } = useUI();
  const { setGlobalFilters } = useNavigation();
  const { user } = useAuth();
  const [selectedModalUser, setSelectedModalUser] = useState<UserProfileData | null>(null);

  const handleOpenProfile = (userData: UserProfileData) => {
    setSelectedModalUser(userData);
  };

  const handleNavigateToFullProfile = (user: UserProfileData) => {
    const isSelf = user.name.toLowerCase().includes('você') || user.name.toLowerCase() === 'você (usuário)';
    if (isSelf) {
      setCurrentView('perfil');
    } else {
      setGlobalFilters((prev: any) => ({
        ...prev,
        memberId: user.id || user.name,
        memberName: user.name,
        memberRole: user.role,
        memberPhoto: user.avatar
      }));
      setCurrentView('membro-workspace');
    }
  };

  const handleMentionUser = (userData: UserProfileData) => {
    setPostText(prev => `${prev} @${userData.name} `.trimStart());
    showToast(`Mencionando @${userData.name} na publicação!`);
  };

  const [localEvents, setLocalEvents] = useState<PulseEventItem[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategoryFilter, setSelectedCategoryFilter] = useState<string>('ALL');
  const [selectedTypeFilter, setSelectedTypeFilter] = useState<string>('ALL');
  const [selectedHashtagFilter, setSelectedHashtagFilter] = useState<string | null>(null);
  const [isComposerOpen, setIsComposerOpen] = useState(false);

  const isExternal = !!externalEvents;

  const baseEvents = isExternal ? externalEvents : localEvents;
  const derivedEvents = baseEvents;

  // Post Creator State
  const [postText, setPostText] = useState('');
  const [postType, setPostType] = useState<PulseEventType>('comment');
  const [selectedNewPostTags, setSelectedNewPostTags] = useState<string[]>([]);
  const [activeReactionPickerId, setActiveReactionPickerId] = useState<string | null>(null);
  const [activeReplyBoxId, setActiveReplyBoxId] = useState<string | null>(null);
  const [replyInputs, setReplyInputs] = useState<Record<string, string>>({});
  const [nestedReplyInputs, setNestedReplyInputs] = useState<Record<string, string>>({});
  const [activeNestedReplyId, setActiveNestedReplyId] = useState<string | null>(null);
  const [toastMessage, setToastMessage] = useState<string | null>(null);
  const [editTarget, setEditTarget] = useState<any | null>(null);
  const [editText, setEditText] = useState('');
  const [deleteTarget, setDeleteTarget] = useState<any | null>(null);
  const [isActionLoading, setIsActionLoading] = useState(false);

  const showToast = (msg: string) => {
    setToastMessage(msg);
    setTimeout(() => setToastMessage(null), 3500);
  };

  // Handle new post publication
  const handlePublishPost = async () => {
    if (!postText.trim()) return;

    const postContent = postText;
    const tags = (selectedNewPostTags || []).length > 0 ? selectedNewPostTags : ['#geral'];

    if (onPublishPost) {
      try {
        await onPublishPost(postContent, postType, tags);
      } catch (err) {
        showToast('❌ Erro ao publicar. Tente novamente.');
      }

      setPostText('');
      setSelectedNewPostTags([]);
      return;
    }

    const newEvent: PulseEventItem = {
      id: `pulse-${Date.now()}`,
      type: postType,
      author: 'Você (Usuário)',
      role: 'Gestor do Workspace',
      company: 'Cyzor Tech',
      time: 'Agora mesmo',
      timestamp: new Date(),
      timelineGroup: 'Hoje',
      category: 'Projetos',
      content: postContent,
      hashtags: tags,
      reactions: {
        agree: 1,
        excellent: 0,
        goodIdea: 0,
        attention: 0,
        approved: 0,
        priority: 0,
        userReactions: ['agree']
      },
      replies: [],
      viewsCount: 1,
      sharesCount: 0,
      moduleSource: 'Projetos'
    };

    setLocalEvents(prev => [newEvent, ...prev]);

    if (onPostCreated) onPostCreated(newEvent);
    setPostText('');
    setSelectedNewPostTags([]);
    showToast('✨ Publicação enviada com sucesso para o Pulse!');
  };

  // Corporate Reaction toggle
  const handleCorporateReact = async (eventId: string, reactionKey: string) => {
    const doLocalToggle = () => {
      if (!isExternal) {
        setLocalEvents(prev =>
          prev.map(ev => {
            if (ev.id !== eventId) return ev;
            const currentReactions = { ...ev.reactions };
            const userReactions = [...(currentReactions.userReactions || [])];
            const hasReacted = userReactions.includes(reactionKey);
            if (hasReacted) {
              currentReactions.userReactions = userReactions.filter(r => r !== reactionKey);
              if (typeof currentReactions[reactionKey] === 'number') (currentReactions[reactionKey] as number) = Math.max(0, (currentReactions[reactionKey] as number) - 1);
            } else {
              currentReactions.userReactions = [...userReactions, reactionKey];
              if (typeof currentReactions[reactionKey] === 'number') (currentReactions[reactionKey] as number) = ((currentReactions[reactionKey] as number) || 0) + 1;
            }
            return { ...ev, reactions: currentReactions };
          })
        );
      }
      setActiveReactionPickerId(null);
    };

    const hasReacted = (derivedEvents.find(e => e.id === eventId)?.reactions?.userReactions || []).includes(reactionKey);

    if (hasReacted) {
      if (onRemoveReaction) onRemoveReaction(eventId, reactionKey);
      doLocalToggle();
    } else {
      if (onAddReaction) {
        try { await onAddReaction(eventId, reactionKey); } catch { /* erro tratado no hook */ }
      }
      doLocalToggle();
    }
  };

  // Toggle Pin
  const handleTogglePin = async (eventId: string) => {
    const current = derivedEvents.find(ev => ev.id === eventId);
    const newPinned = !current?.isPinned;
    try {
      await onTogglePin?.(eventId, newPinned);
      setLocalEvents(prev => prev.map(ev => ev.id === eventId ? { ...ev, isPinned: newPinned } : ev));
      showToast(newPinned ? '📌 Publicação fixada no topo do Pulse!' : 'Publicação desafixada.');
    } catch {
      showToast('Não foi possível atualizar o pin da publicação.');
    }
  };

  // Add Reply (Level 1)
  const handleAddReply = async (eventId: string) => {
    const text = replyInputs[eventId];
    if (!text || !text.trim()) return;

    const newReply: PulseReply = {
      id: `rep-${Date.now()}`,
      author: user?.displayName || user?.name || 'Você (Usuário)',
      role: (user as any)?.role || 'Gestor do Workspace',
      time: 'Agora mesmo',
      text: text.trim(),
      replies: []
    };

    const addReplyToEvent = (ev: PulseEventItem) => {
      if (ev.id !== eventId) return ev;
      return { ...ev, replies: [...(ev.replies || []), newReply] };
    };

    if (!isExternal) {
      setLocalEvents(prev => prev.map(addReplyToEvent));
    }
    if (onAddComment) await onAddComment(eventId, text.trim());

    setReplyInputs(prev => ({ ...prev, [eventId]: '' }));
    showToast('💬 Resposta adicionada!');
  };

  // Add Nested Reply (Level 2 - Max depth limit)
  const handleAddNestedReply = async (eventId: string, replyId: string) => {
    const text = nestedReplyInputs[replyId];
    if (!text || !text.trim()) return;

    const subReply: PulseReply = {
      id: `subrep-${Date.now()}`,
      author: 'Você (Usuário)',
      role: 'Gestor do Workspace',
      time: 'Agora mesmo',
      text: text.trim()
    };

    setLocalEvents(prev =>
      prev.map(ev => {
        if (ev.id !== eventId) return ev;
        return {
          ...ev,
          replies: (ev.replies || []).map(r => {
            if (r.id !== replyId) return r;
            return {
              ...r,
              replies: [...(r.replies || []), subReply]
            };
          })
        };
      })
    );

    setNestedReplyInputs(prev => ({ ...prev, [replyId]: '' }));
    if (onAddComment) await onAddComment(eventId, text.trim(), replyId);
    setActiveNestedReplyId(null);
    showToast('💬 Resposta encadeada enviada!');
  };

  // Filter logic
  const filteredEvents = useMemo(() => {
    return (derivedEvents || []).filter(ev => {
      if (filterModule && ev.moduleSource !== filterModule) return false;
      if (selectedCategoryFilter !== 'ALL' && ev.category !== selectedCategoryFilter) return false;
      if (selectedTypeFilter !== 'ALL' && ev.type !== selectedTypeFilter) return false;
      if (selectedHashtagFilter && !(ev.hashtags || []).includes(selectedHashtagFilter)) return false;
      if (searchQuery.trim()) {
        const q = searchQuery.toLowerCase();
        const matchContent = (ev.content || '').toLowerCase().includes(q);
        const matchAuthor = (ev.author || '').toLowerCase().includes(q);
        const matchRole = (ev.role || '').toLowerCase().includes(q);
        const matchTags = (ev.hashtags || []).some(t => t && t.toLowerCase().includes(q));
        if (!matchContent && !matchAuthor && !matchRole && !matchTags) return false;
      }
      return true;
    });
  }, [derivedEvents, filterModule, selectedCategoryFilter, selectedTypeFilter, selectedHashtagFilter, searchQuery]);

  return (
    <div className="flex flex-col gap-6 w-full relative">
      <AnimatePresence>
        {editTarget && (
          <motion.div className="fixed inset-0 z-[100] flex items-center justify-center bg-slate-950/30 p-4 backdrop-blur-sm" initial={{opacity: 0}} animate={{opacity: 1}} exit={{opacity: 0}}>
            <motion.div className="w-full max-w-lg rounded-2xl border border-slate-200 bg-white p-5 shadow-2xl" initial={{y: 15, scale: .98}} animate={{y: 0, scale: 1}}>
              <div className="flex items-center justify-between border-b border-slate-100 pb-3"><h3 className="text-sm font-black text-slate-900">Editar publicação</h3><button type="button" onClick={() => setEditTarget(null)}><X size={17}/></button></div>
              <textarea value={editText} onChange={e => setEditText(e.target.value)} rows={5} className="mt-4 w-full rounded-xl border border-slate-200 bg-slate-50 p-3 text-sm outline-none focus:border-indigo-500" />
              <div className="mt-4 flex justify-end gap-2"><button type="button" onClick={() => setEditTarget(null)} className="rounded-xl border border-slate-200 px-3 py-2 text-xs font-bold text-slate-600">Cancelar</button><button type="button" disabled={isActionLoading || !editText.trim()} onClick={async () => { try { setIsActionLoading(true); await onEditPost?.(editTarget.id, editText.trim()); setEditTarget(null); showToast('Publicação atualizada.'); } catch { showToast('Não foi possível editar a publicação.'); } finally { setIsActionLoading(false); } }} className="rounded-xl bg-slate-900 px-3 py-2 text-xs font-bold text-white disabled:opacity-50">Salvar alterações</button></div>
            </motion.div>
          </motion.div>
        )}
        {deleteTarget && (
          <motion.div className="fixed inset-0 z-[100] flex items-center justify-center bg-slate-950/30 p-4 backdrop-blur-sm" initial={{opacity: 0}} animate={{opacity: 1}} exit={{opacity: 0}}>
            <motion.div className="w-full max-w-sm rounded-2xl border border-slate-200 bg-white p-5 shadow-2xl" initial={{y: 15, scale: .98}} animate={{y: 0, scale: 1}}>
              <h3 className="text-sm font-black text-slate-900">Excluir publicação?</h3><p className="mt-2 text-xs leading-relaxed text-slate-500">Esta ação removerá a publicação do Pulse. Ela não poderá ser recuperada pela interface.</p>
              <div className="mt-5 flex justify-end gap-2"><button type="button" onClick={() => setDeleteTarget(null)} className="rounded-xl border border-slate-200 px-3 py-2 text-xs font-bold text-slate-600">Cancelar</button><button type="button" disabled={isActionLoading} onClick={async () => { try { setIsActionLoading(true); await onDeletePost?.(deleteTarget.id); setDeleteTarget(null); showToast('Publicação excluída.'); } catch { showToast('Não foi possível excluir a publicação.'); } finally { setIsActionLoading(false); } }} className="rounded-xl bg-red-600 px-3 py-2 text-xs font-bold text-white disabled:opacity-50">Excluir publicação</button></div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
      {/* Toast Notification */}
      <AnimatePresence>
        {toastMessage && (
          <motion.div
            initial={{ opacity: 0, y: -20, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -20, scale: 0.95 }}
            className="fixed top-20 right-8 z-50 bg-[#0F172A] text-white px-4 py-2.5 rounded-xl shadow-xl text-xs font-bold flex items-center gap-2 border border-slate-700"
          >
            <Sparkles size={14} className="text-amber-400" />
            <span>{toastMessage}</span>
          </motion.div>
        )}
      </AnimatePresence>

      <div className="rounded-[20px] border border-slate-200/80 bg-[linear-gradient(135deg,_rgba(99,102,241,0.06),_rgba(255,255,255,1)_30%,_rgba(248,250,252,1))] p-3.5 shadow-[0_12px_24px_rgba(15,23,42,0.03)]">
        {!isComposerOpen ? (
          <button
            type="button"
            onClick={() => setIsComposerOpen(true)}
            className="flex w-full items-center gap-3 rounded-[16px] border border-slate-200 bg-white/80 px-3 py-3 text-left transition hover:border-slate-300 hover:bg-white"
          >
            <div className="flex h-10 w-10 items-center justify-center rounded-full bg-[linear-gradient(135deg,_#0f172a,_#334155)] text-sm font-bold text-white shadow-[0_10px_18px_rgba(15,23,42,0.18)]">
              {user?.displayName?.slice(0, 2)?.toUpperCase() || user?.name?.slice(0, 2)?.toUpperCase() || 'VC'}
            </div>
            <div className="flex-1 text-left">
              <div className="text-[12px] font-bold text-[#0F172A]">O que está acontecendo no Pulse?</div>
              <div className="text-[10px] text-slate-500">Criar publicação</div>
            </div>
            <span className="inline-flex items-center gap-1 rounded-xl bg-slate-900 px-2.5 py-1.5 text-[10px] font-bold text-white">
              <Plus size={12} />
              Nova
            </span>
          </button>
        ) : (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/25 p-4 backdrop-blur-[2px]">
            <motion.div
              initial={{ opacity: 0, y: 16, scale: 0.98 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: 16, scale: 0.98 }}
              className="w-full max-w-2xl rounded-[24px] border border-slate-200 bg-white p-4 shadow-[0_30px_80px_rgba(15,23,42,0.18)]"
            >
              <div className="flex items-center justify-between border-b border-slate-100 pb-3">
                <div className="flex items-center gap-3">
                  <div className="flex h-10 w-10 items-center justify-center rounded-full bg-[linear-gradient(135deg,_#0f172a,_#334155)] text-sm font-bold text-white">
                    {user?.displayName?.slice(0, 2)?.toUpperCase() || user?.name?.slice(0, 2)?.toUpperCase() || 'VC'}
                  </div>
                  <div>
                    <div className="text-[12px] font-bold text-[#0F172A]">Criar publicação</div>
                    <div className="text-[10px] text-slate-500">{user?.displayName || user?.name || 'Usuário'} no Pulse</div>
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => setIsComposerOpen(false)}
                  className="rounded-lg border border-slate-200 bg-slate-50 px-2 py-1 text-[10px] font-bold text-slate-600 hover:bg-slate-100"
                >
                  Fechar
                </button>
              </div>

              <textarea
                value={postText}
                onChange={e => setPostText(e.target.value)}
                placeholder="O que está acontecendo na sua empresa?"
                rows={5}
                className="mt-4 w-full resize-none rounded-[16px] border border-slate-200 bg-slate-50 p-3.5 text-xs font-medium text-slate-900 placeholder:text-slate-400 focus:border-indigo-500 focus:bg-white focus:outline-none focus:ring-4 focus:ring-indigo-100"
              />

              <div className="mt-3 flex flex-wrap items-center gap-1.5">
                {['#sprint34', '#arquitetura', '#vendas', '#ia', '#release', '#financeiro'].map(tag => {
                  const isSelected = selectedNewPostTags.includes(tag);
                  return (
                    <button
                      key={tag}
                      type="button"
                      onClick={() => {
                        if (isSelected) {
                          setSelectedNewPostTags(prev => prev.filter(t => t !== tag));
                        } else {
                          setSelectedNewPostTags(prev => [...prev, tag]);
                        }
                      }}
                      className={`rounded-full border px-2.5 py-1 text-[10px] font-mono font-bold transition ${
                        isSelected
                          ? 'border-indigo-600 bg-indigo-600 text-white shadow-[0_8px_16px_rgba(79,70,229,0.25)]'
                          : 'border-slate-200 bg-white text-slate-700 hover:border-slate-300 hover:bg-slate-100'
                      }`}
                    >
                      {tag}
                    </button>
                  );
                })}
              </div>

              <div className="mt-4 flex flex-wrap items-center justify-between gap-2 border-t border-slate-100 pt-3">
                <div className="flex flex-wrap items-center gap-1.5">
                  {[
                    { id: 'comment', label: 'Comentário', emoji: '💬' },
                    { id: 'idea', label: 'Ideia', emoji: '💡' },
                    { id: 'decision', label: 'Decisão', emoji: '⚡' },
                    { id: 'indicator', label: 'Métrica', emoji: '📊' },
                    { id: 'deploy', label: 'Deploy', emoji: '🚀' },
                    { id: 'event', label: 'Evento', emoji: '📅' }
                  ].map(t => (
                    <button
                      key={t.id}
                      type="button"
                      onClick={() => setPostType(t.id as PulseEventType)}
                      className={`rounded-xl border px-2.5 py-1.5 text-[11px] font-bold transition ${
                        postType === t.id
                          ? 'border-[#0F172A] bg-[#0F172A] text-white shadow-[0_8px_16px_rgba(15,23,42,0.14)]'
                          : 'border-slate-200 bg-white text-slate-600 hover:border-slate-300 hover:bg-slate-50'
                      }`}
                    >
                      <span>{t.emoji}</span> {t.label}
                    </button>
                  ))}
                </div>

                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={() => {
                      setIsComposerOpen(false);
                      setPostText('');
                      setSelectedNewPostTags([]);
                    }}
                    className="rounded-xl border border-slate-200 bg-white px-3 py-2 text-[11px] font-bold text-slate-700 hover:bg-slate-50"
                  >
                    Cancelar
                  </button>
                  <button
                    type="button"
                    onClick={async () => {
                      await handlePublishPost();
                      setIsComposerOpen(false);
                    }}
                    disabled={!postText.trim()}
                    className="inline-flex items-center gap-1.5 rounded-xl bg-[linear-gradient(135deg,_#4f46e5,_#4338ca)] px-3.5 py-2 text-[11px] font-bold text-white shadow-[0_10px_20px_rgba(79,70,229,0.28)] transition hover:brightness-105 disabled:cursor-not-allowed disabled:opacity-40"
                  >
                    <Send size={13} />
                    Publicar
                  </button>
                </div>
              </div>
            </motion.div>
          </div>
        )}
      </div>

      <div className="rounded-[18px] border border-slate-200/75 bg-white p-3 shadow-[0_8px_18px_rgba(15,23,42,0.02)]">
        <div className="relative">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <input
            type="text"
            value={searchQuery}
            onChange={e => setSearchQuery(e.target.value)}
            placeholder="Buscar no Pulse..."
            className="w-full rounded-xl border border-slate-200 bg-slate-50 py-2.5 pl-9 pr-8 text-xs font-medium text-slate-900 placeholder:text-slate-400 focus:border-indigo-500 focus:bg-white focus:outline-none focus:ring-4 focus:ring-indigo-100"
          />
          {searchQuery && (
            <button
              type="button"
              onClick={() => setSearchQuery('')}
              className="absolute right-2.5 top-1/2 -translate-y-1/2 text-[10px] font-bold text-slate-400 hover:text-slate-700"
            >
              ✕
            </button>
          )}
        </div>

        <div className="mt-3 flex flex-wrap items-center gap-1.5">
          {[
            { id: 'ALL', label: 'Tudo', icon: Layers, count: derivedEvents.length },
            { id: 'decision', label: 'Decisões', icon: ShieldCheck, count: derivedEvents.filter(e => e.type === 'decision').length },
            { id: 'idea', label: 'Ideias', icon: Lightbulb, count: derivedEvents.filter(e => e.type === 'idea').length },
            { id: 'client', label: 'Clientes', icon: Building2, count: derivedEvents.filter(e => e.type === 'client').length },
            { id: 'deploy', label: 'Deploys', icon: Rocket, count: derivedEvents.filter(e => e.type === 'deploy').length },
            { id: 'ai', label: 'IA', icon: Cpu, count: derivedEvents.filter(e => e.type === 'ai').length }
          ].map(f => {
            const IconComp = f.icon;
            return (
              <button
                key={f.id}
                type="button"
                onClick={() => setSelectedTypeFilter(f.id)}
                className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1.5 text-[10px] font-bold transition ${
                  selectedTypeFilter === f.id
                    ? 'border-[#0F172A] bg-[#0F172A] text-white shadow-[0_8px_16px_rgba(15,23,42,0.14)]'
                    : 'border-slate-200 bg-slate-50 text-slate-600 hover:border-slate-300 hover:bg-slate-100'
                }`}
              >
                <IconComp size={11} />
                {f.label}
              </button>
            );
          })}
        </div>
      </div>

      {/* Loading State */}
      {isLoading && externalEvents === undefined && (
        <div className="flex flex-col gap-4">
          {[1, 2, 3].map(i => (
            <div key={i} className="p-5 rounded-[20px] bg-white border border-[#0F172A0F] flex flex-col gap-3 animate-pulse">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-slate-200 shrink-0" />
                <div className="flex flex-col gap-1.5 flex-1">
                  <div className="h-3 w-32 bg-slate-200 rounded" />
                  <div className="h-2.5 w-48 bg-slate-100 rounded" />
                </div>
              </div>
              <div className="flex flex-col gap-1.5 pl-1">
                <div className="h-2.5 w-full bg-slate-100 rounded" />
                <div className="h-2.5 w-4/5 bg-slate-100 rounded" />
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Error State */}
      {feedError && externalEvents === undefined && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="p-10 rounded-[20px] bg-white border border-rose-100 text-center flex flex-col items-center justify-center gap-3"
        >
          <div className="w-12 h-12 rounded-2xl bg-rose-50 flex items-center justify-center text-rose-500">
            <AlertTriangle size={22} />
          </div>
          <span className="text-sm font-bold text-[#0F172A]">Erro ao carregar o feed do Pulse</span>
          <span className="text-xs text-slate-500 font-medium max-w-sm">
            {(feedError as any)?.message || 'Não foi possível carregar as atividades. Verifique sua conexão.'}
          </span>
          {onRetryFeed && (
            <button
              type="button"
              onClick={onRetryFeed}
              className="mt-1 px-4 py-2 rounded-xl bg-[#0F172A] text-white font-bold text-xs shadow-2xs hover:bg-slate-800 transition-all cursor-pointer"
            >
              Tentar novamente
            </button>
          )}
        </motion.div>
      )}

      {/* Main Feed Events List */}
      <div className="flex flex-col gap-6">
        <AnimatePresence mode="popLayout">
          {filteredEvents.length === 0 ? (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="p-10 rounded-[20px] bg-white border border-[#0F172A0F] text-center flex flex-col items-center justify-center gap-2"
            >
              <div className="w-12 h-12 rounded-2xl bg-slate-100 flex items-center justify-center text-slate-400">
                <Search size={22} />
              </div>
              <span className="text-sm font-bold text-[#0F172A]">Nenhum evento encontrado no Pulse</span>
              <span className="text-xs text-slate-500 font-medium max-w-sm">
                Tente ajustar os termos da pesquisa ou selecione outro tipo de filtro na barra lateral.
              </span>
            </motion.div>
          ) : (
            (['Hoje', 'Ontem', 'Esta Semana', 'Este Mês', 'Mais Antigos'] as const).map(group => {
              const groupEvents = filteredEvents.filter(
                e => e.timelineGroup === group || (!e.timelineGroup && group === 'Mais Antigos')
              );
              if (groupEvents.length === 0) return null;

              return (
                <div key={group} className="flex flex-col gap-4">
                  {/* Timeline Divider */}
                  <div className="flex items-center gap-3 py-1">
                    <span className="text-[10px] font-mono font-bold uppercase tracking-widest text-slate-400 whitespace-nowrap">
                      {group}
                    </span>
                    <div className="h-[1px] bg-[#0F172A0F] flex-1" />
                  </div>

                  {/* Render Events */}
                  {groupEvents.map(eventItem => {
                    const authorName = String(eventItem.authorName || eventItem.author || 'Usuário');
                    const authorRole = String(eventItem.authorRole || eventItem.role || 'Membro');
                    const authorAvatar = eventItem.authorAvatar || eventItem.avatar || '';
                    const eventContent = String(eventItem.content || eventItem.metadata?.content || '');
                    const typeCfg = EVENT_TYPE_CONFIG[eventItem.type] || EVENT_TYPE_CONFIG.comment;
                    const IconComp = typeCfg.icon;
                    const isExpanded = activeReplyBoxId === eventItem.id;
                    const showReactionPicker = activeReactionPickerId === eventItem.id;

                    return (
                      <motion.div
                        key={eventItem.id}
                        layout
                        initial={{ opacity: 0, y: 12 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, scale: 0.98 }}
                        className={`relative flex flex-col gap-3 rounded-[20px] border bg-[linear-gradient(180deg,_rgba(255,255,255,0.96),_rgba(248,250,252,0.92))] p-4 shadow-[0_12px_26px_rgba(15,23,42,0.03)] transition duration-200 hover:-translate-y-0.5 hover:shadow-[0_18px_32px_rgba(15,23,42,0.06)] ${
                          eventItem.isPinned
                            ? 'border-indigo-200 bg-indigo-50/25'
                            : 'border-slate-200/80'
                        }`}
                      >
                        {/* Pinned Badge */}
                        {eventItem.isPinned && (
                          <div className="absolute top-3 right-4 flex items-center gap-1 text-[10px] font-bold text-indigo-600 bg-indigo-50 px-2 py-0.5 rounded-md border border-indigo-100">
                            <Pin size={10} className="rotate-45 fill-indigo-600" />
                            <span>Fixado</span>
                          </div>
                        )}

                        {/* Author Header */}
                        <div className="flex items-start justify-between gap-3">
                          <div
                            onClick={() =>
handleOpenProfile({
                                 id: eventItem.actorId || eventItem.id,
                                 name: eventItem.authorName || eventItem.author || 'Usuário',
                                 role: eventItem.authorRole || eventItem.role || '',
                                 company: eventItem.company,
                                 avatar: eventItem.authorAvatar || eventItem.avatar
                               })
                            }
                            className="flex items-center gap-3 cursor-pointer group"
                            title={`Ver perfil de ${authorName}`}
                          >
                            <div className="relative h-10 w-10 overflow-hidden rounded-full border border-slate-200 bg-slate-100 shadow-[0_8px_16px_rgba(15,23,42,0.08)] transition-all group-hover:scale-[1.02] group-hover:border-indigo-400">
                              {authorAvatar ? (
                                <img
                                  src={authorAvatar}
                                  alt={eventItem.author}
                                  className="h-full w-full object-cover"
                                />
                              ) : (
                                <div className="flex h-full w-full items-center justify-center text-xs font-extrabold text-slate-700">
                                  {authorName.charAt(0)}
                                </div>
                              )}
                              <span className="absolute bottom-0 right-0 block h-2.5 w-2.5 rounded-full border-2 border-white bg-emerald-500" />
                            </div>

                            <div className="flex flex-col">
                              <div className="flex items-center gap-1.5 flex-wrap">
                                <span className="text-xs sm:text-sm font-bold text-[#0F172A] group-hover:text-indigo-600 transition-colors leading-tight">
                                  {authorName}
                                </span>
                                <span className="text-[10px] text-slate-400">•</span>
                                <span className="text-[10px] font-medium text-slate-500">
                                  {eventItem.time}
                                </span>
                              </div>
                              <span className="text-[10px] text-slate-500 font-medium">
                                {eventItem.role} {eventItem.company ? `• ${eventItem.company}` : ''}
                              </span>
                            </div>
                          </div>

                          <div className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-[10px] font-bold ${typeCfg.badgeBg} ${typeCfg.badgeText} ${typeCfg.badgeBorder}`}>
                            <IconComp size={12} strokeWidth={2.2} />
                            <span>{typeCfg.label}</span>
                          </div>
                        </div>

                        {/* Event Body Text */}
                        <div className="pl-1 flex flex-col gap-3">
                          <p className="text-sm leading-6 text-slate-800 whitespace-pre-line [text-wrap:pretty]">
                            {eventContent}
                          </p>

                          {/* Specific Component Attachments / Indicators / Deploys */}
                          {eventItem.indicator && (
                            <div className="p-3.5 rounded-xl bg-slate-50 border border-[#0F172A0A] max-w-sm flex items-center justify-between">
                              <div className="flex flex-col gap-0.5">
                                <span className="text-[9px] font-bold uppercase tracking-wider text-slate-400">
                                  Métrica Monitorada
                                </span>
                                <span className="text-xs font-bold text-slate-800">
                                  {eventItem.indicator.metric}
                                </span>
                              </div>
                              <div className="flex items-center gap-2">
                                <span className="text-base font-mono font-bold text-indigo-600">
                                  {eventItem.indicator.value}
                                </span>
                                <span className="text-[10px] font-mono font-bold bg-emerald-50 text-emerald-700 px-1.5 py-0.5 rounded border border-emerald-200/60">
                                  {eventItem.indicator.change}
                                </span>
                              </div>
                            </div>
                          )}

                          {eventItem.decision && (
                            <div className="p-3.5 rounded-xl bg-purple-50/40 border border-purple-100/80 flex flex-col gap-1.5 text-xs">
                              <div className="flex justify-between items-center">
                                <span className="text-[9px] font-bold uppercase tracking-wider text-purple-700">
                                  Homologação Executiva
                                </span>
                                <span className="text-[10px] font-bold bg-purple-100 text-purple-800 px-2 py-0.5 rounded-md">
                                  Impacto {eventItem.decision.impact}
                                </span>
                              </div>
                              <span className="text-slate-700 font-medium">
                                Decidido por <strong>{eventItem.decision.decidedBy}</strong>
                              </span>
                              {eventItem.decision.relationships && (
                                <div className="flex flex-wrap gap-1 mt-0.5">
                                  {eventItem.decision.relationships.map((rel, idx) => (
                                    <span
                                      key={idx}
                                      className="text-[9.5px] font-bold bg-white text-purple-700 px-2 py-0.5 rounded border border-purple-200/60"
                                    >
                                      {rel}
                                    </span>
                                  ))}
                                </div>
                              )}
                            </div>
                          )}

                          {eventItem.attachments && eventItem.attachments.length > 0 && (
                            <div className="flex flex-col gap-2 max-w-md">
                              {eventItem.attachments.map((file, fIdx) => (
                                <div
                                  key={fIdx}
                                  className="p-3 rounded-xl bg-slate-50 border border-[#0F172A0A] flex items-center justify-between"
                                >
                                  <div className="flex items-center gap-2.5 min-w-0">
                                    <div className="w-8 h-8 rounded-lg bg-sky-50 text-sky-700 flex items-center justify-center border border-sky-100 shrink-0">
                                      <FileText size={16} />
                                    </div>
                                    <div className="flex flex-col min-w-0">
                                      <span className="text-xs font-bold text-slate-800 truncate">
                                        {file.name}
                                      </span>
                                      <span className="text-[9px] text-slate-400 font-medium">
                                        {file.size}
                                      </span>
                                    </div>
                                  </div>
                                  <button
                                    type="button"
                                    onClick={() => showToast(`📥 Baixando ${file.name}...`)}
                                    className="text-[10px] font-bold text-indigo-600 hover:underline px-2 py-1 cursor-pointer"
                                  >
                                    Baixar
                                  </button>
                                </div>
                              ))}
                            </div>
                          )}

                          {/* Hashtag Chips */}
                          {eventItem.hashtags && eventItem.hashtags.length > 0 && (
                            <div className="flex flex-wrap gap-1 mt-1">
                              {eventItem.hashtags.map(tag => (
                                <button
                                  key={tag}
                                  type="button"
                                  onClick={() =>
                                    setSelectedHashtagFilter(
                                      selectedHashtagFilter === tag ? null : tag
                                    )
                                  }
                                  className={`px-2 py-0.5 rounded-md text-[10px] font-mono font-bold transition-all cursor-pointer border ${
                                    selectedHashtagFilter === tag
                                      ? 'bg-indigo-600 text-white border-indigo-600'
                                      : 'bg-indigo-50/60 hover:bg-indigo-50 text-indigo-700 border-indigo-100'
                                  }`}
                                >
                                  {tag}
                                </button>
                              ))}
                            </div>
                          )}
                        </div>

                        {/* Reaction Totals & Views Bar */}
                        <div className="flex items-center justify-between border-t border-slate-100 pt-2.5 text-[10px] text-slate-400 font-bold px-1">
                          <div className="flex items-center gap-2">
                            <span className="flex -space-x-1.5 items-center">
                              {CORPORATE_REACTIONS.map(react => {
                                const count = (eventItem.reactions as any)?.[react.key] || 0;
                                if (count > 0) {
                                  const Icon = react.icon;
                                  return (
                                    <span key={react.key} className={`w-5 h-5 rounded-full flex items-center justify-center border-2 border-white ${react.bg} ${react.color}`}>
                                      <Icon size={10} strokeWidth={3} />
                                    </span>
                                  );
                                }
                                return null;
                              })}
                            </span>
                            <span className="text-slate-500 font-medium">
                              {Object.entries(eventItem.reactions || {})
                                .filter(([k, v]) => k !== 'userReactions' && typeof v === 'number')
                                .reduce((acc, [_, v]) => acc + (v as number), 0)}{' '}
                              reações corporativas
                            </span>
                          </div>

                          <div className="flex items-center gap-3">
                            <span className="font-medium">{eventItem.viewsCount} visualizações</span>
                            <button
                              type="button"
                              onClick={() => setActiveReplyBoxId(isExpanded ? null : eventItem.id)}
                              className="text-slate-600 hover:text-indigo-600 font-bold hover:underline cursor-pointer"
                            >
                              {eventItem.replies && eventItem.replies.length > 0
                                ? `${eventItem.replies.length} respostas`
                                : 'Responder'}
                            </button>
                          </div>
                        </div>

                        {/* Action Buttons Row */}
                        <div className="grid grid-cols-2 gap-1.5 border-t border-b border-slate-100 py-1.5 text-[11px] font-bold text-slate-600 sm:grid-cols-4">
                          {/* Reaction Popover Button */}
                          <div className="relative flex justify-center">
                            <button
                              type="button"
                              onClick={() =>
                                setActiveReactionPickerId(showReactionPicker ? null : eventItem.id)
                              }
                              className={`flex items-center justify-center gap-1.5 py-1.5 rounded-xl hover:bg-slate-50 w-full cursor-pointer transition-all ${
                                eventItem.reactions?.userReactions?.length
                                  ? 'text-indigo-600 font-bold'
                                  : 'hover:text-slate-900'
                              }`}
                            >
                              <ThumbsUp size={14} className={eventItem.reactions?.userReactions?.length ? 'fill-indigo-100' : ''} />
                              <span>Reagir</span>
                            </button>

                            {/* Corporate Emoji Popover */}
                            {showReactionPicker && (
                              <div className="absolute bottom-10 left-1/2 -translate-x-1/2 bg-white p-1.5 rounded-xl shadow-lg border border-slate-200 flex gap-1 z-30 animate-in fade-in slide-in-from-bottom-2 duration-150">
                                {CORPORATE_REACTIONS.map(react => {
                                  const isReacted = eventItem.reactions?.userReactions?.includes(
                                    react.key
                                  );
                                  const Icon = react.icon;
                                  return (
                                    <button
                                      key={react.key}
                                      type="button"
                                      onClick={() =>
                                        handleCorporateReact(
                                          eventItem.id,
                                          react.key as keyof PulseReaction
                                        )
                                      }
                                      className={`w-8 h-8 rounded-lg flex items-center justify-center transition-all cursor-pointer border ${
                                        isReacted
                                          ? `${react.bg} ${react.color} ${react.borderColor}`
                                          : `border-transparent text-slate-500 hover:bg-slate-100 hover:text-slate-900`
                                      }`}
                                      title={react.label}
                                    >
                                      <Icon size={16} strokeWidth={isReacted ? 2.5 : 2} />
                                    </button>
                                  );
                                })}
                              </div>
                            )}
                          </div>

                          <button
                            type="button"
                            onClick={() => setActiveReplyBoxId(isExpanded ? null : eventItem.id)}
                            className="flex items-center justify-center gap-1 py-1.5 rounded-xl hover:bg-slate-50 hover:text-slate-900 cursor-pointer transition-all"
                          >
                            <MessageSquare size={13} />
                            <span>Responder</span>
                          </button>

                          <button
                            type="button"
                            onClick={() => handleTogglePin(eventItem.id)}
                            className={`flex items-center justify-center gap-1 py-1.5 rounded-xl hover:bg-slate-50 cursor-pointer transition-all ${
                              eventItem.isPinned ? 'text-indigo-600' : 'hover:text-slate-900'
                            }`}
                          >
                            <Pin size={13} className={eventItem.isPinned ? 'rotate-45 fill-indigo-600' : ''} />
                            <span>Fixar</span>
                          </button>

                          <button
                            type="button"
                            onClick={() => {
                              navigator.clipboard?.writeText(window.location.href);
                              showToast('🔗 Link da atividade copiado!');
                            }}
                            className="flex items-center justify-center gap-1 py-1.5 rounded-xl hover:bg-slate-50 hover:text-slate-900 cursor-pointer transition-all"
                          >
                            <Share2 size={13} />
                            <span>Compartilhar</span>
                          </button>

                          {eventItem.sourceEntityType === 'PULSE_POST' && eventItem.actorId === user?.id && (
                            <>
                              <button
                                type="button"
                                onClick={async () => {
                                  setEditTarget(eventItem); setEditText(eventItem.content || '');
                                }}
                                className="flex items-center justify-center gap-1 py-1.5 rounded-xl hover:bg-slate-50 hover:text-slate-900 cursor-pointer transition-all"
                              >Editar</button>
                              <button
                                type="button"
                                onClick={async () => {
                                  setDeleteTarget(eventItem);
                                }}
                                className="flex items-center justify-center gap-1 py-1.5 rounded-xl hover:bg-red-50 hover:text-red-600 cursor-pointer transition-all"
                              >Excluir</button>
                            </>
                          )}
                        </div>

                        {/* Thread Replies Section */}
                        {(isExpanded || (eventItem.replies && eventItem.replies.length > 0)) && (
                          <div className="flex flex-col gap-3 pl-2 sm:pl-4 mt-1 border-l-2 border-slate-100">
                            {eventItem.replies && eventItem.replies.length > 0 && (
                              <div className="flex flex-col gap-3">
                                {eventItem.replies.map(reply => (
                                  <div key={reply.id} className="flex flex-col gap-2 pl-3">
                                    <div
                                      onClick={() =>
                                        handleOpenProfile({
                                          name: reply.author,
                                          role: reply.role || 'Colaborador',
                                          avatar: reply.avatar
                                        })
                                      }
                                      className="flex items-center gap-2 cursor-pointer group"
                                      title={`Ver perfil de ${reply.author}`}
                                    >
                                      <div className="w-6 h-6 rounded-full bg-slate-100 border border-slate-200 group-hover:border-indigo-500 flex items-center justify-center text-[10px] font-bold text-slate-700 overflow-hidden shrink-0">
                                        {reply.avatar ? (
                                          <img src={reply.avatar} alt={reply.author} className="w-full h-full object-cover" />
                                        ) : (
                                          String(reply.author || 'U').charAt(0)
                                        )}
                                      </div>
                                      <span className="text-xs font-bold text-[#0F172A] group-hover:text-indigo-600 transition-colors">
                                        {reply.author}
                                      </span>
                                      <span className="text-[10px] text-slate-400">•</span>
                                      <span className="text-[10px] text-slate-500">{reply.time}</span>
                                    </div>
                                    <p className="text-xs text-slate-700 font-medium pl-8">
                                      {reply.text}
                                    </p>

                                    {/* Sub-replies (Level 2) */}
                                    {reply.replies && reply.replies.length > 0 && (
                                      <div className="flex flex-col gap-2 pl-8 mt-1 border-l border-slate-200/80">
                                        {reply.replies.map(subR => (
                                          <div key={subR.id} className="flex flex-col gap-1 pl-2">
                                            <div className="flex items-center gap-2">
                                              <span className="text-[11px] font-bold text-[#0F172A]">
                                                {subR.author}
                                              </span>
                                              <span className="text-[9px] text-slate-400">
                                                {subR.time}
                                              </span>
                                            </div>
                                            <p className="text-[11px] text-slate-600 font-medium">
                                              {subR.text}
                                            </p>
                                          </div>
                                        ))}
                                      </div>
                                    )}

                                    {/* Button to add nested reply */}
                                    {activeNestedReplyId === reply.id ? (
                                      <div className="flex gap-2 pl-8 mt-1">
                                        <input
                                          type="text"
                                          value={nestedReplyInputs[reply.id] || ''}
                                          onChange={e =>
                                            setNestedReplyInputs(prev => ({
                                              ...prev,
                                              [reply.id]: e.target.value
                                            }))
                                          }
                                          placeholder="Responder a esta mensagem..."
                                          className="flex-1 px-3 py-1 bg-slate-50 border border-slate-200 rounded-lg text-xs font-medium focus:outline-none"
                                        />
                                        <button
                                          type="button"
                                          onClick={() => handleAddNestedReply(eventItem.id, reply.id)}
                                          className="px-2.5 py-1 bg-indigo-600 text-white rounded-lg text-xs font-bold"
                                        >
                                          Enviar
                                        </button>
                                      </div>
                                    ) : (
                                      <button
                                        type="button"
                                        onClick={() => setActiveNestedReplyId(reply.id)}
                                        className="text-[10px] text-indigo-600 font-bold hover:underline pl-8 self-start cursor-pointer"
                                      >
                                        Responder
                                      </button>
                                    )}
                                  </div>
                                ))}
                              </div>
                            )}

                            {/* Reply Input (Level 1) */}
                            <div className="flex items-center gap-2 mt-2">
                              <input
                                type="text"
                                value={replyInputs[eventItem.id] || ''}
                                onChange={e =>
                                  setReplyInputs(prev => ({ ...prev, [eventItem.id]: e.target.value }))
                                }
                                onKeyDown={e => {
                                  if (e.key === 'Enter') handleAddReply(eventItem.id);
                                }}
                                placeholder="Escreva uma resposta para a equipe..."
                                className="flex-1 px-3 py-2 bg-slate-50 focus:bg-white border border-[#0F172A10] focus:border-indigo-600 rounded-xl text-xs font-medium text-slate-900 placeholder:text-slate-400 focus:outline-none transition-all"
                              />
                              <button
                                type="button"
                                onClick={() => handleAddReply(eventItem.id)}
                                className="px-3.5 py-2 bg-[#0F172A] hover:bg-slate-800 text-white rounded-xl text-xs font-bold transition-all cursor-pointer shadow-2xs"
                              >
                                Responder
                              </button>
                            </div>
                          </div>
                        )}
                      </motion.div>
                    );
                  })}
                </div>
              );
            })
          )}
        </AnimatePresence>
      </div>

      {/* User Profile Card Modal */}
      <UserProfileModal
        user={selectedModalUser}
        onClose={() => setSelectedModalUser(null)}
        onNavigateToFullProfile={handleNavigateToFullProfile}
        onMentionUser={handleMentionUser}
      />
    </div>
  );
}
